import React, { useState, useEffect } from "react";
import Sidebar from "../Dashboard/Sidebar";
import Header from "../Dashboard/Header";
import Footer from "../Dashboard/Footer";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Github, FolderPlus, Save } from "lucide-react";

function CreateProject() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem, setActiveSidebarItem] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [formData, setFormData] = useState({
    projectName: '',
    githubUrl: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLargeScreen = windowWidth >= 992;

  const getSidebarWidth = () => {
    if (!sidebarOpen) return '0px';
    return sidebarCollapsed ? '80px' : '280px';
  };

  const handleSidebarItemClick = (index) => {
    setActiveSidebarItem(index);
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleUserDropdown = () => setUserDropdownOpen(!userDropdownOpen);

  const handleSidebarCollapseChange = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

      useEffect(() => {
            document.title = "DCode | Create project";
          }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const isValidGithubUrl = (url) => {
    const githubUrlPattern = /^https?:\/\/(www\.)?github\.com\/[\w\-\.]+\/[\w\-\.]+\/?$/;
    return githubUrlPattern.test(url);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.projectName.trim()) {
      newErrors.projectName = 'Project name is required';
    } else if (formData.projectName.trim().length < 3) {
      newErrors.projectName = 'Project name must be at least 3 characters';
    }

    if (formData.githubUrl && !isValidGithubUrl(formData.githubUrl)) {
      newErrors.githubUrl = 'Please enter a valid GitHub repository URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('access_token');
      const email = localStorage.getItem('Email');

      const response = await fetch(`${BACKEND_URL}/projects/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          project_name: formData.projectName,
          github_url: formData.githubUrl || null,  // Send null if empty
          description: formData.description || null,
          email: email,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setFormData({
          projectName: '',
          githubUrl: '',
          description: ''
        });
        alert(data.message);
        navigate('/project/view');
      } else {
        const errorMessages = data.errors
          ? Object.entries(data.errors).map(([field, messages]) => `${field}: ${messages.join(', ')}`).join('; ')
          : data.message || 'Failed to create project';
        throw new Error(errorMessages);
      }

    } catch (error) {
      console.error('Error creating project:', error);
      alert(`Failed to create project: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/project/view");
  };

  return (
    <div className="d-flex flex-column min-vh-100">
      <div
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : '0px',
          transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1030
        }}
      >
        <Header
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
          userDropdownOpen={userDropdownOpen}
          toggleUserDropdown={toggleUserDropdown}
          pageTitle="Create project"
        />
      </div>

      <Sidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        activeItem={activeSidebarItem}
        onItemClick={handleSidebarItemClick}
        onCollapseChange={handleSidebarCollapseChange}
      />

      {sidebarOpen && !isLargeScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
          onClick={toggleSidebar}
        ></div>
      )}

      <main
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : '0px',
          paddingTop: '70px',
          transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: 'calc(100vh - 140px)'
        }}
      >
        <div className="container-fluid p-4">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h2 className="mb-0 text-dark fw-bold">Create new project</h2>
              <p className="text-muted mb-0 mt-1">Set up your project with a name and optional repository</p>
            </div>
            <button
              className="btn d-flex align-items-center"
              style={{ background: "#75c4c0", color: "white" }}
              onClick={handleCancel}
            >
              <ArrowLeft size={18} className="me-1" />
              Back
            </button>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-10">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white border-bottom-0 py-4">
                  <div className="d-flex align-items-center">
                    <div className="bg-opacity-10 rounded-circle p-3 me-3">
                      <FolderPlus size={24} style={{ color: "#75c4c0" }} />
                    </div>
                    <div>
                      <h4 className="mb-0 text-dark fw-semibold">Project details</h4>
                      <p className="text-muted mb-0">Fill in the information below to create your project</p>
                    </div>
                  </div>
                </div>

                <div className="card-body p-4">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label htmlFor="projectName" className="form-label fw-semibold text-dark">
                        Project Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control form-control-lg ${errors.projectName ? 'is-invalid' : ''}`}
                        id="projectName"
                        name="projectName"
                        value={formData.projectName}
                        onChange={handleInputChange}
                        placeholder="Enter your project name"
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          fontSize: '16px'
                        }}
                      />
                      {errors.projectName && (
                        <div className="invalid-feedback d-block">
                          {errors.projectName}
                        </div>
                      )}
                      <small className="form-text text-muted">
                        Choose a descriptive name for your project (minimum 3 characters)
                      </small>
                    </div>

                    <div className="mb-4">
                      <label htmlFor="githubUrl" className="form-label fw-semibold text-dark">
                        Repository URL (Optional)
                      </label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-light border-end-0" style={{ borderRadius: '8px 0 0 8px', border: '2px solid #e9ecef' }}>
                          <Github size={20} className="text-muted" />
                        </span>
                        <input
                          type="url"
                          className={`form-control border-start-0 ${errors.githubUrl ? 'is-invalid' : ''}`}
                          id="githubUrl"
                          name="githubUrl"
                          value={formData.githubUrl}
                          onChange={handleInputChange}
                          placeholder="https://github.com/username/repository"
                          style={{
                            borderRadius: '0 8px 8px 0',
                            border: '2px solid #e9ecef',
                            fontSize: '16px'
                          }}
                        />
                        {errors.githubUrl && (
                          <div className="invalid-feedback d-block">
                            {errors.githubUrl}
                          </div>
                        )}
                      </div>
                      <small className="form-text text-muted">
                        Enter the complete URL of your repository (e.g., https://github.com/user/repo), or leave blank
                      </small>
                    </div>

                    <div className="d-flex gap-3 pt-3">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="upload-button"
                        style={{
                          borderRadius: '8px',
                          fontWeight: '500'
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            Creating...
                          </>
                        ) : (
                          <>
                            <Save size={18} className="me-2" />
                            Create project
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              <div className="card mt-4 bg-light border-0">
                <div className="card-body p-4">
                  <h6 className="fw-semibold text-dark mb-3">
                    <Github size={18} className="me-2" />
                    Notes
                  </h6>
                  <ul className="list-unstyled mb-0 text-muted">
                    <li className="mb-2">• Make sure you have the required access permissions for the specified repository</li>
                    <li>• You can update the repository URL later if needed</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
}

export default CreateProject;